import React, { useState, useRef } from 'react';
import { Box, Button, Typography, Paper, List, CircularProgress } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ImageIcon from '@mui/icons-material/Image';

const ImageUploader = ({ onImagesProcessed, loading }) => {
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    
    // Add new files to the images array
    setImages((prevImages) => [
      ...prevImages,
      ...newFiles.map((file, index) => ({
        id: `${Date.now()}-${index}`,
        file,
        preview: URL.createObjectURL(file)
      }))
    ]);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(images);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setImages(items);
  };

  const handleRemoveImage = (id) => {
    setImages(images.filter((image) => image.id !== id));
  };

  const handleProcess = () => {
    if (images.length > 0) {
      const imagesToProcess = images.map(img => img.file);
      onImagesProcessed(imagesToProcess);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Upload NEET Question Paper Images
      </Typography>
      
      <Box 
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          mb: 2,
          backgroundColor: '#f9f9f9',
          cursor: 'pointer'
        }}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          Click to upload or drag and drop
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Supported formats: JPG, PNG, GIF
        </Typography>
      </Box>

      {images.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Uploaded Images ({images.length})
          </Typography>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="images">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ mb: 3 }}
                >
                  {images.map((image, index) => (
                    <Draggable key={image.id} draggableId={image.id} index={index}>
                      {(provided) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          elevation={1}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            mb: 1,
                            backgroundColor: '#f5f5f5',
                            '&:hover': { backgroundColor: '#eeeeee' }
                          }}
                        >
                          <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                            <DragHandleIcon />
                          </Box>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              mr: 2,
                              backgroundImage: `url(${image.preview})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              borderRadius: 1
                            }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" noWrap>
                              {image.file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {(image.file.size / 1024).toFixed(2)} KB
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            onClick={() => handleRemoveImage(image.id)}
                            startIcon={<DeleteIcon />}
                            color="error"
                          >
                            Remove
                          </Button>
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => fileInputRef.current.click()}
            >
              Add More Images
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProcess}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                  Processing...
                </>
              ) : (
                'Process Images'
              )}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ImageUploader;
